import {
    Col,
    Row,
    Typography,
    Form,
    Input,
    Space,
    Button,
    message,
    Modal,
    Switch,
    Alert,
} from "antd";
import {useEffect, useState} from "react";
import {useNavigate, useOutletContext} from "react-router-dom";
import { ActivityOutletContext } from "../../interface";
import {deleteMatchActivity, updateMatchActivity} from "../../apiBridge";
import dayjs from 'dayjs';
import ImportModal from "./ImportModal";
import ActivityLogoUpload from "./_activityLogoUpload";

const { Text, Title, Link } = Typography;

const EditActivity: React.FC = () => {
    const data = useOutletContext() as ActivityOutletContext

    const [form] = Form.useForm();
    const [messageApi, contextHolder] = message.useMessage();

    const navigate = useNavigate()

    const [loadingState, setLoadingState] = useState(false);
    const [importModalVisible, setImportModalVisible] = useState(false); 

    const deleteActivity = async () => {
        let inputValue = '';
        Modal.confirm({
            title: 'Delete Activity',
            content: (
                <div>
                    <p>Are you sure you want to delete this activity? This action cannot be undone.</p>
                    <p>Please enter the activity name to confirm deletion:</p>
                    <Input 
                        onChange={(e) => inputValue = e.target.value}
                        placeholder="Enter activity name"
                    />
                </div>
            ),
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                if (!inputValue) {
                    messageApi.error('Please enter activity name');
                    return false;
                }
                if (inputValue !== data.activity.name) {
                    messageApi.error('Activity name does not match');
                    return false;
                }
                setLoadingState(true);
                try {
                    const res = await deleteMatchActivity(data.activity.id, data.activity.name);
                    if (res) {
                        const resp = await res.json();
                        if (resp?.error) {
                            messageApi.error(resp.error);
                            return false;
                        } else {
                            messageApi.success("Activity deleted successfully");
                            navigate(`/activity/`);
                            return true;
                        }
                    }
                } catch (error) {
                    messageApi.error('Failed to delete activity');
                    return false;
                } finally {
                    setLoadingState(false);
                }
                return false;
            }
        });
    }

    const doUpdateSave = async (values: any) => {
        const payload = {
            name: values.name,
            display_name: values.display_name,
            description: values.description ? values.description : '',
            allow_register: values.allow_register,
            display_policy: values.public ? 'public' : 'private',
            logo: values.logo || ""
        }

        setLoadingState(true);
        try {
            const res = await updateMatchActivity(data.activity.id, payload)
            if (res && res.status === 200) {
                messageApi.success("Activity update successfully")
                data.reloadData("info")
            } else {
                const resp = await res.json();
                if (resp?.error) {
                    messageApi.error(resp.error);
                    return false;
                } else {
                    messageApi.error('Failed to update activity');
                    return false;
                }
            }
        } catch (error) {
            messageApi.error('Failed to update activity');
            return false;
        } finally {
            setLoadingState(false);
        }
    };

    useEffect(() => {
        const activity = data.activity;
        form.setFieldsValue({
            name: activity.name,
            description: activity.description,
            location:activity.options.location,
            start_date: dayjs(activity.start_at),
            end_date: dayjs(activity.end_at),
            allow_register: activity.allow_register,
            display_name: activity.options.display_name,
            logo: activity.options.logo,
            'public': activity.display_policy === 'public',
        });
    }, [data.activity, form]);

    return (
        <>
            {contextHolder}

            {(!data.categories || data.categories.length === 0) && (
                <Alert
                    message="No categories found"
                    description={
                        <span>
                            Please{' '}
                            <Link onClick={() => setImportModalVisible(true)} style={{ cursor: 'pointer' }}>
                                import 
                            </Link>{' '}
                            to create categories.
                        </span>
                    }
                    type="info"
                    style={{ marginBottom: 20 }}
                />
            )}

            <Form
                form={form}
                name="editActivityForm"
                layout="vertical"
                onFinish={doUpdateSave}
            >
                <div style={{ background: "white", borderRadius: 4, boxShadow: "0 0 3px 0 #cbcbcb", padding: 20 }}>
                    <Title level={4}>Basic Information</Title>
                    <Row gutter={24}>
                        <Col sm={12} xs={24}>
                            <Form.Item label="Tournament Name" rules={[{ required: true }]} required={true} name="name">
                                <Input />
                            </Form.Item>
                        </Col>

                        <Col sm={6} xs={24}>
                            <Form.Item label="Display Name" name="display_name">
                                 <Input />
                            </Form.Item>
                        </Col>

                        <Col sm={12} xs={24}>
                            <Form.Item label="Tournament Description" name="description">
                                <Input.TextArea />
                            </Form.Item>
                        </Col>

                        <Col sm={12}>
                            <Form.Item label="Logo" name="logo">
                                <ActivityLogoUpload confirmRemove />
                            </Form.Item>
                        </Col>

                        <Col sm={12} xs={24} style={{"display": "none"}}>
                            <Row gutter={24}>
                                <Col xs={12}>
                                    <Form.Item label="Registration Status" name="allow_register">
                                        <Switch />
                                    </Form.Item>
                                </Col>

                                <Col xs={12}>
                                    <Form.Item label="Public" name="public">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </div>

                {data.activity.expired_at && (
                    <Col span={24}>
                        <Alert 
                            message={`This activity will be deleted after ${data.activity.expired_at}`}
                            type="info"
                            style={{ marginTop: 16 }}
                        />
                    </Col>
                )}

                <div style={{ background: "white", borderRadius: 4, boxShadow: "0 0 3px 0 #cbcbcb", marginTop: 20, padding: 20 }}>
                    <Title level={4}>Tournament Details</Title>
                    <Row gutter={[24, 24]}>
                        <Col sm={8} xs={24}>
                            <div>
                                <Text type="secondary">Tournament Location</Text>
                                <div style={{ marginTop: 4 }}>
                                    <Text strong>{data.activity.options.location}</Text>
                                </div>
                            </div>
                        </Col>


                        <Col sm={8} xs={24}>
                            <div>
                                <Text type="secondary">Start Date</Text>
                                <div style={{ marginTop: 4 }}>
                                    <Text strong>{dayjs(data.activity.start_at).format('YYYY-MM-DD')}</Text>
                                </div>
                            </div>
                        </Col>

                        <Col sm={8} xs={24}>
                            <div>
                                <Text type="secondary">End Date</Text>
                                <div style={{ marginTop: 4 }}>
                                    <Text strong>{dayjs(data.activity.end_at).format('YYYY-MM-DD')}</Text>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>

                <div style={{ background: "white", borderRadius: 4, boxShadow: "0 0 3px 0 #cbcbcb", marginTop: 20, padding: 20 }}>
                    <Title level={4}>Match Schedule</Title>
                    {!data.activity.options.start_time || data.activity.options.start_time.length === 0 ? (
                        <div>No match schedule set</div>
                    ) : (
                        <Row gutter={[24, 24]}>
                            {data.activity.options.start_time.map((matchTime, index) => (
                                <Col key={index} sm={6} xs={12}>
                                    <div>
                                        <Text type="secondary">{matchTime.date}</Text>
                                        <div style={{ marginTop: 4 }}>
                                            <Text strong>{matchTime.start_time}</Text>
                                        </div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    )}
                </div>

                <Form.Item style={{ marginTop: 20 }}>
                    <Space>
                        <Button type="primary" htmlType="submit" loading={loadingState}>
                            Update
                        </Button>
                    </Space>

                    <div style={{ float: "right" }}>
                        <Space>
                            <Button onClick={() => setImportModalVisible(true)}>
                                Import 
                            </Button>
                            <Button danger onClick={() => deleteActivity()} loading={loadingState}>Delete</Button>
                        </Space>
                    </div>
                </Form.Item>
            </Form>

            <ImportModal
                activity={data.activity}
                visible={importModalVisible}
                onClose={() => setImportModalVisible(false)}
                onSuccess={() => {
                    data.reloadData("participants");
                }}
                categories={data.categories}
            />
        </>
    );
};

export default EditActivity
